/**
 * Framework Versions API Client
 * Platform Admin only - manages compliance frameworks and their versions
 */

import {
  Framework,
  FrameworkVersion,
  FrameworkWithVersions,
  CreateFrameworkDTO,
  UpdateFrameworkDTO,
  CreateFrameworkVersionDTO,
  UpdateFrameworkVersionDTO,
  ApiResponse
} from './frameworkVersions.dto';

/**
 * API Base Configuration
 */
const API_BASE = '/api/compliance/definition/framework-versions';

/**
 * Get authentication headers (including CSRF token for mutating requests)
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  };
}

/**
 * Handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || error.error || `API Error: ${response.status}`);
  }
  return response.json();
}

/**
 * Framework Versions API Client
 *
 * Uses proper framework version management endpoints:
 * - GET /api/compliance/definition/framework-versions
 * - GET /api/compliance/definition/framework-versions/:id
 * - POST /api/compliance/definition/framework-versions
 * - PUT /api/compliance/definition/framework-versions/:id
 * - DELETE /api/compliance/definition/framework-versions/:id
 * - GET /api/compliance/definition/framework-versions/:id/versions
 * - GET /api/compliance/definition/framework-versions/versions/active
 * - POST /api/compliance/definition/framework-versions/versions
 * - PUT /api/compliance/definition/framework-versions/versions/:id
 * - POST /api/compliance/definition/framework-versions/versions/:id/activate
 * - POST /api/compliance/definition/framework-versions/versions/:id/deprecate
 * - DELETE /api/compliance/definition/framework-versions/versions/:id
 */
export const frameworkVersionsApi = {
  // ==========================================================================
  // FRAMEWORK ENDPOINTS
  // ==========================================================================

  /**
   * Get all frameworks with their versions
   * GET /api/compliance/definition/framework-versions
   */
  async getAllFrameworks(): Promise<FrameworkWithVersions[]> {
    const response = await fetch(`${API_BASE}`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkWithVersions[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get a single framework by ID with its versions
   * GET /api/compliance/definition/framework-versions/:id
   */
  async getFrameworkById(id: number): Promise<FrameworkWithVersions> {
    const response = await fetch(`${API_BASE}/${id}`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkWithVersions> = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new framework
   * POST /api/compliance/definition/framework-versions
   */
  async createFramework(data: CreateFrameworkDTO): Promise<Framework> {
    const response = await fetch(`${API_BASE}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<Framework> = await handleResponse(response);
    return result.data;
  },

  /**
   * Update an existing framework
   * PUT /api/compliance/definition/framework-versions/:id
   */
  async updateFramework(id: number, data: UpdateFrameworkDTO): Promise<Framework> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<Framework> = await handleResponse(response);
    return result.data;
  },

  /**
   * Delete a framework
   * DELETE /api/compliance/definition/framework-versions/:id
   */
  async deleteFramework(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },

  // ==========================================================================
  // FRAMEWORK VERSION ENDPOINTS
  // ==========================================================================

  /**
   * Get all versions for a framework
   * GET /api/compliance/definition/framework-versions/:id/versions
   */
  async getFrameworkVersions(frameworkId: number): Promise<FrameworkVersion[]> {
    const response = await fetch(`${API_BASE}/${frameworkId}/versions`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkVersion[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Get active framework versions (for Framework Mapping)
   * GET /api/compliance/definition/framework-versions/versions/active
   */
  async getActiveFrameworkVersions(): Promise<FrameworkVersion[]> {
    const response = await fetch(`${API_BASE}/versions/active`, {
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkVersion[]> = await handleResponse(response);
    return result.data;
  },

  /**
   * Create a new framework version
   * POST /api/compliance/definition/framework-versions/versions
   */
  async createFrameworkVersion(data: CreateFrameworkVersionDTO): Promise<FrameworkVersion> {
    const response = await fetch(`${API_BASE}/versions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<FrameworkVersion> = await handleResponse(response);
    return result.data;
  },

  /**
   * Update a framework version
   * PUT /api/compliance/definition/framework-versions/versions/:id
   */
  async updateFrameworkVersion(id: number, data: UpdateFrameworkVersionDTO): Promise<FrameworkVersion> {
    const response = await fetch(`${API_BASE}/versions/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    const result: ApiResponse<FrameworkVersion> = await handleResponse(response);
    return result.data;
  },

  /**
   * Check activation impact before activating
   * GET /api/compliance/definition/framework-versions/versions/:id/activation-impact
   */
  async getActivationImpact(id: number): Promise<{
    hasActiveAssignments: boolean;
    currentActiveVersionId: number | null;
    currentActiveVersionName: string | null;
    affectedAssignments: Array<{ assignmentId: number; companyId: number; companyName: string; status: string }>;
  }> {
    const response = await fetch(`${API_BASE}/versions/${id}/activation-impact`, {
      headers: getAuthHeaders(),
    });
    const result: ApiResponse<any> = await handleResponse(response);
    return result.data;
  },

  /**
   * Activate a framework version
   * POST /api/compliance/definition/framework-versions/versions/:id/activate
   * Pass force=true to proceed when there are active company assignments
   */
  async activateFrameworkVersion(id: number, force?: boolean): Promise<FrameworkVersion> {
    const response = await fetch(`${API_BASE}/versions/${id}/activate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ force: force || false }),
    });

    if (response.status === 409) {
      const result = await response.json();
      const err: any = new Error(result.message || 'Activation blocked');
      err.code = 'ACTIVATION_IMPACT';
      err.impact = result.data;
      throw err;
    }

    const result: ApiResponse<FrameworkVersion> = await handleResponse(response);
    return result.data;
  },

  /**
   * Deprecate a framework version
   * POST /api/compliance/definition/framework-versions/versions/:id/deprecate
   */
  async deprecateFrameworkVersion(id: number): Promise<FrameworkVersion> {
    const response = await fetch(`${API_BASE}/versions/${id}/deprecate`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    const result: ApiResponse<FrameworkVersion> = await handleResponse(response);
    return result.data;
  },

  /**
   * Delete a framework version
   * DELETE /api/compliance/definition/framework-versions/versions/:id
   */
  async deleteFrameworkVersion(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/versions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },
};
